import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Grid, CircularProgress } from '@mui/material';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttTable,
    SttFormDate,
    SttFormText,
    SttButton,
    SttFormPeriod,
    SttButtonFilter,
    SttFullDialog,
} from '../../../sporttia/all';
import PaymentModal from '../../../components/paymentModal/PaymentModal';
import { getPath } from '../../Pages';
import { getErrorMessage } from '../../../lib/utils';
import useScsService from '../../../services/ScsService';
import { usePermission } from '../../../lib/usePermission';
import { PERMISSIONS } from '../../../lib/permissions';

type Payment = {
    id: number;
    concept: string;
    ini: string;
    end: string;
    price: number;
    user: {
        name: string;
        mship?: {
            name: string;
        };
    };
};

type PaymentsResponse = {
    rows: Payment[];
    count: number;
    page: number;
    pages: number;
};

type Filter = {
    concept: string;
    useIni: string;
    useEnd: string;
};

export default function GenerateRemittence(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { can } = usePermission();
    const canGenerate = can(PERMISSIONS.REMITTANCE.CREATE);
    const navigate = useNavigate();
    const scsService = useScsService();
    const [payments, setPayments] = useState<PaymentsResponse>(
        {} as PaymentsResponse,
    );
    const [filter, setFilter] = useState<Filter>({
        concept: '',
        useIni: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
        useEnd: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
    });
    const [paymentDialogId, setPaymentDialogId] = useState<number>();
    const [paymentDate, setPaymentDate] = useState<string>(
        format(new Date(), 'yyyy-MM-dd'),
    );
    const createMutation = useMutation({
        mutationFn: ({
            id,
            params,
        }: {
            id: number;
            params: Record<string, unknown>;
        }) =>
            scsService.createScsRemittences(id, params) as Promise<{
                remittence?: { id: number };
            }>,
        onSuccess: (data: any) =>
            navigate(getPath('remittence' as any, { id: data.remittence?.id })),
        onError: (error: unknown) => {
            cxt?.showMessage('E', getErrorMessage(error));
        },
    });

    const handleForm = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void => {
        if (!name) return;
        setFilter({ ...filter, [name]: value as string });
    };

    const loadPayments = (p = { page: 1, rows: 20 }): void => {
        cxt?.api('GET', `/scs/${Number(cxt?.sc?.id)}/remittences/payments`, {
            params: {
                ...filter,
                page: p.page,
                rows: p.rows,
            },
            success: (r: PaymentsResponse) => {
                setPayments(r);
            },
        });
    };

    return (
        <SttFullDialog
            open
            onClose={() => navigate(getPath('remittences' as any))}
            title={t('GenerateRemittence')}
        >
            <Paper>
                <Box p={3}>
                    <Grid container spacing={3}>
                        <SttFormText
                            grid
                            md={3}
                            sm={12}
                            xs={12}
                            name="concept"
                            caption={t('Concept')}
                            defVal={filter.concept}
                            onChange={handleForm}
                        />

                        <SttFormPeriod
                            grid
                            md={6}
                            sm={12}
                            xs={12}
                            caption={t('Period')}
                            nameIni="useIni"
                            nameEnd="useEnd"
                            defValIni={filter.useIni}
                            defValEnd={filter.useEnd}
                            onChange={handleForm}
                        />

                        <SttFormDate
                            grid
                            md={3}
                            sm={12}
                            xs={12}
                            caption={t('Issue')}
                            defVal={paymentDate}
                            onChange={({
                                value,
                            }: {
                                name: string | undefined;
                                value: unknown;
                            }) => setPaymentDate(value as string)}
                        />
                    </Grid>
                </Box>
            </Paper>

            <Box mt={2}>
                <Grid container spacing={3}>
                    <Grid size={{ md: 3, sm: 12, xs: 12 }}>
                        <SttButtonFilter
                            fullWidth
                            onClick={() => loadPayments()}
                        />
                    </Grid>

                    {canGenerate && (
                        <Grid size={{ md: 3, sm: 12, xs: 12 }}>
                            <SttButton
                                fullWidth
                                variant="contained"
                                color="primary"
                                disabled={createMutation.isPending}
                                startIcon={
                                    createMutation.isPending ? (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />
                                    ) : (
                                        <FlashOnIcon />
                                    )
                                }
                                caption={
                                    createMutation.isPending
                                        ? t('Generating')
                                        : t('Generate')
                                }
                                confirmation
                                onClick={() =>
                                    createMutation.mutate({
                                        id: Number(cxt?.sc?.id),
                                        // #86944gzv0 En v7 todos los parameters se enviarán por BODY.
                                        // Eliminar los query string cuando el EP esté subido a PRO.
                                        params: {
                                            concept: filter.concept,
                                            paymentDate,
                                            useIni: filter.useIni,
                                            useEnd: filter.useEnd,
                                        },
                                    } as any)
                                }
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Box mt={4}>
                <Paper>
                    {payments && (
                        <SttTable
                            data={payments}
                            loading={false}
                            columns={[
                                {
                                    type: 'link',
                                    title: t('Concept'),
                                    align: 'left',
                                    field: 'concept',
                                    onClick: (row: Payment) =>
                                        setPaymentDialogId(row.id),
                                },
                                {
                                    title: t('Period'),
                                    type: 'periodPlain',
                                    value: (row: Payment) => ({
                                        ini: row.ini,
                                        end: row.end,
                                    }),
                                },
                                {
                                    title: t('User'),
                                    width: 160,
                                    align: 'left',
                                    value: (row: Payment) =>
                                        row.user?.mship
                                            ? row.user.mship.name
                                            : row.user?.name,
                                },
                                {
                                    title: t('Price'),
                                    type: 'price',
                                    field: 'price',
                                },
                            ]}
                            onFetch={loadPayments}
                        />
                    )}
                </Paper>
            </Box>

            {paymentDialogId && (
                <PaymentModal
                    idPayment={paymentDialogId}
                    onClose={() => setPaymentDialogId(undefined)}
                />
            )}
        </SttFullDialog>
    );
}
